// aifab - PredictModal
// Handles both standard classification/regression prediction and DAPT fill-mask testing.
import { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { apiFetch } from '../../hooks/apiFetch';
import { Modal, Spinner, ModalBody, ModalFooter } from '../common';

const cleanError = (msg) => msg ? msg.replace(/^\[ErrorClass\.\w+\]\s*/, '') : msg;
// Classical ML families take tabular input; every other family (transformer,
// LLM, embedding - including any newly-added one) is text. Defaulting unknown
// families to text means a new HuggingFace model works here with no code change.
// A new *classical* algorithm needs a code recipe anyway, so it's added here too.
const TABULAR_FAMILIES = new Set(['XGBOOST','RANDOM_FOREST','LIGHTGBM','CATBOOST','LOGISTIC_REG','SVM']);

// Generic fill-mask examples - applicable to any domain
// The model will predict the [MASK] token based on what it learned from your corpus.
const SAMPLE_MASKS = [
  "The [MASK] was reported and assigned to the relevant team.",
  "Unable to access the system due to a [MASK] error.",
  "The request was marked as [MASK] and escalated.",
  "Performance issues were detected on the [MASK] component.",
  "The [MASK] process failed and requires immediate attention.",
];


// ── Predict History ───────────────────────────────────────────────────────────
function PredictHistory({ versionId }) {
  const { data, loading } = useApi(`/models/${versionId}/predict/history`);
  const rows = data || [];
  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>;
  if (!rows.length) return (
    <div style={{ textAlign:'center', color:'#64748B', fontSize:13, padding:'40px 0', lineHeight:1.8 }}>
      No predictions yet.<br />
      <span style={{ fontSize:12 }}>Use the Predict tab to test the model.</span>
    </div>
  );

  // Extract the human-readable input text from input_data JSONB
  const getInputText = (r) => {
    const d = r.input_data || {};
    // Try common field names: text, input, sentence, query, features (joined)
    if (d.text)     return String(d.text);
    if (d.input)    return String(d.input);
    if (d.sentence) return String(d.sentence);
    if (d.query)    return String(d.query);
    // For tabular / multi-feature input, show key:value pairs
    const entries = Object.entries(d).filter(([k]) => k !== 'top_k');
    if (entries.length) return entries.map(([k,v]) => `${k}: ${v}`).join(' · ');
    return '-';
  };

  return (
    <div style={{ overflowY:'auto', maxHeight:460 }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr style={{ borderBottom:'1px solid #E4EBF5', background:'#F7F9FC' }}>
            {['Time','Input','Prediction','Status','ms'].map(h => (
              <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700,
                fontSize:10, textTransform:'uppercase', letterSpacing:'.08em', color:'#64748B' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const pred = typeof r.prediction === 'object'
              ? JSON.stringify(r.prediction) : String(r.prediction ?? r.error ?? '-');
            const inputText = getInputText(r);
            const ok = r.status === 'success' || r.status === 'COMPLETED';
            return (
              <tr key={r.request_id || i} style={{
                borderBottom:'1px solid #F0F4F8',
                background: i%2===0 ? '#FFFFFF' : '#F7F9FC',
              }}>
                {/* Time */}
                <td style={{ padding:'9px 12px', color:'#94A3B8', fontFamily:'var(--f-mono)', fontSize:11, whiteSpace:'nowrap' }}>
                  {r.requested_at ? new Date(r.requested_at).toLocaleTimeString() : '-'}
                </td>
                {/* Input — the question / sentence / features */}
                <td style={{ padding:'9px 12px', maxWidth:200 }} title={inputText}>
                  <div style={{
                    fontFamily:'var(--f-mono)', fontSize:11, color:'#2D4059',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                    background:'#F0F4F8', borderRadius:4, padding:'3px 7px',
                  }}>
                    {inputText.length > 60 ? inputText.slice(0,60)+'…' : inputText}
                  </div>
                </td>
                {/* Prediction output */}
                <td style={{ padding:'9px 12px', maxWidth:220 }} title={pred}>
                  <div style={{
                    fontFamily:'var(--f-mono)', fontSize:11,
                    color: ok ? '#065F46' : '#991B1B',
                    background: ok ? 'rgba(5,150,105,.07)' : 'rgba(220,38,38,.07)',
                    borderRadius:4, padding:'3px 7px',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                  }}>
                    {pred.length > 65 ? pred.slice(0,65)+'…' : pred}
                  </div>
                </td>
                {/* Status badge */}
                <td style={{ padding:'9px 12px' }}>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:99,
                    background: ok ? 'rgba(5,150,105,.12)' : 'rgba(220,38,38,.12)',
                    color: ok ? '#059669' : '#DC2626',
                    border: `1px solid ${ok ? 'rgba(5,150,105,.2)' : 'rgba(220,38,38,.2)'}`,
                  }}>{ok ? 'OK' : 'FAIL'}</span>
                </td>
                {/* Duration */}
                <td style={{ padding:'9px 12px', color:'#94A3B8', fontFamily:'var(--f-mono)', fontSize:11 }}>
                  {r.duration_ms != null ? r.duration_ms : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── DAPT fill-mask modal ──────────────────────────────────────────────────────
function DaptModal({ versionId, onClose, runLabel, runNumber }) {
  const [activeTab, setActiveTab] = useState('predict');
  const [text,    setText]    = useState('');
  const [topK,    setTopK]    = useState(5);
  const [compare, setCompare] = useState(false);
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);

  const run = async () => {
    if (!text.trim() || !text.includes('[MASK]')) {
      setErr('Enter a sentence containing [MASK]. Example: "The [MASK] ticket was escalated."');
      return;
    }
    setLoading(true); setErr(null); setResult(null);
    try {
      const res = await apiFetch(`/models/${versionId}/predict`, {
        method: 'POST',
        body: JSON.stringify({
          data: { text, top_k: topK, compare: compare ? 'true' : 'false' },
          timeout_secs: compare ? 180 : 120,
        }),
      });
      setResult(res);
    } catch (e) { setErr(cleanError(e.message)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Test Domain-Adapted Model" onClose={onClose} xl>
      <ModalBody>
      {/* Tab bar */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--bd)', marginBottom:16, marginTop:-4 }}>
        {[['predict','▶ Fill-Mask'],['history','History']].map(([tid,tlabel]) => (
          <button key={tid} onClick={() => setActiveTab(tid)}
            style={{ padding:'8px 18px', fontSize:12, fontWeight:600, background:'none', border:'none',
              cursor:'pointer', color: activeTab===tid ? 'var(--or)' : '#64748B',
              borderBottom: activeTab===tid ? '2px solid var(--or)' : '2px solid transparent',
              marginBottom:-1 }}>{tlabel}</button>
        ))}
      </div>

      {activeTab === 'history' ? (
        <PredictHistory versionId={versionId} />
      ) : (
        <>
          {/* Run badge */}
          {(runLabel || runNumber) && (
            <div style={{ marginBottom:16, padding:'8px 12px', background:'#F1F5FB',
              borderRadius:6, borderLeft:'3px solid var(--or)',
              display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:11, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em', fontWeight:600 }}>Run</span>
              {runNumber && <span style={{ fontSize:12, color:'var(--or)', fontFamily:'var(--f-mono)', fontWeight:700 }}>#{runNumber}</span>}
              {runLabel  && <span style={{ fontSize:12, color:'#334155' }}>{runLabel}</span>}
              <span style={{ marginLeft:'auto', fontSize:10, fontFamily:'var(--f-mono)', color:'#64748B' }}>DOMAIN_PRETRAIN</span>
            </div>
          )}

          {/* Info banner */}
          <div style={{ marginBottom:16, padding:'10px 14px', background:'rgba(59,130,246,.07)',
            border:'1px solid rgba(59,130,246,.2)', borderRadius:6, fontSize:12, color:'#334155', lineHeight:1.6 }}>
            <strong style={{ color:'#60a5fa' }}>Fill-mask test.</strong>{' '}
            This model learned your domain vocabulary during pre-training. Type a sentence with{' '}
            <code style={{ fontFamily:'var(--f-mono)', color:'var(--or)' }}>[MASK]</code> and see what it predicts.
            It is not a classifier - use the output as a signal that domain adaptation worked.
          </div>

          <div style={{ display:'flex', gap:16, minHeight:380 }}>
            {/* Left: input */}
            <div style={{ width:340, flexShrink:0, borderRight:'1px solid var(--bd)', display:'flex', flexDirection:'column' }}>
              <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--bd)',
                fontSize:10, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em', fontWeight:700 }}>
                Input
              </div>

              <div style={{ flex:1, padding:'14px 16px', display:'flex', flexDirection:'column', gap:12 }}>
                {/* Sample sentences */}
                <div>
                  <div style={{ fontSize:10, color:'#64748B', marginBottom:6 }}>Sample sentences</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                    {SAMPLE_MASKS.map(s => (
                      <button key={s} onClick={() => setText(s)}
                        style={{ textAlign:'left', padding:'5px 8px', fontSize:11,
                          background: text === s ? 'rgba(232,71,10,.12)' : '#F1F5FB',
                          border: text === s ? '1px solid rgba(232,71,10,.3)' : '1px solid #C8D5E8',
                          borderRadius:4, color: text === s ? 'var(--or)' : '#334155',
                          cursor:'pointer', lineHeight:1.4 }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Free text input */}
                <div>
                  <div style={{ fontSize:10, color:'#64748B', marginBottom:6 }}>Or type your own</div>
                  <textarea rows={3} value={text} onChange={e => setText(e.target.value)}
                    placeholder={'The [MASK] was reported and assigned to the team.'}
                    style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px',
                      background:'#F7F9FC', border:'1px solid var(--bd2)', borderRadius:4,
                      color:'#0F172A', fontFamily:'var(--f-ui)', fontSize:12, lineHeight:1.5,
                      resize:'vertical', outline:'none' }}
                    onFocus={e => e.target.style.borderColor='#E8470A'}
                    onBlur={e  => e.target.style.borderColor='#B0C0D8'}
                  />
                </div>

                {/* Options */}
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:10, color:'#64748B', marginBottom:4 }}>Top-K</div>
                    <input type="number" min={1} max={10} value={topK}
                      onChange={e => setTopK(+e.target.value)}
                      style={{ width:60, padding:'5px 7px', background:'#F7F9FC',
                        border:'1px solid var(--bd2)', borderRadius:4,
                        color:'#0F172A', fontFamily:'var(--f-mono)', fontSize:12, outline:'none' }} />
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4, paddingTop:16 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#334155', cursor:'pointer' }}>
                      <input type="checkbox" checked={compare} onChange={e => setCompare(e.target.checked)} />
                      Compare with base model
                    </label>
                    {compare && (
                      <div style={{ fontSize:10, color:'#fbbf24', background:'rgba(251,191,36,.08)',
                        border:'1px solid rgba(251,191,36,.2)', borderRadius:3, padding:'4px 8px', lineHeight:1.5 }}>
                        Loads 2 models (~520MB extra RAM).
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ padding:'12px 16px', borderTop:'1px solid var(--bd)' }}>
                <button onClick={run} disabled={loading}
                  className="btn btn-primary btn-sm" style={{ width:'100%' }}>
                  {loading ? <><Spinner size={11}/> Running…</> : '▶ Run Fill-Mask'}
                </button>
              </div>
            </div>

            {/* Right: results */}
            <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
              <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--bd)',
                fontSize:10, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em', fontWeight:700 }}>
                Predictions
              </div>

              <div style={{ flex:1, padding:'16px', display:'flex', flexDirection:'column', gap:10 }}>
                {err && (
                  <div style={{ display:'flex', gap:8, padding:'10px 12px',
                    background:'rgba(220,38,38,.08)', border:'1px solid rgba(220,38,38,.25)', borderRadius:6 }}>
                    <span style={{ color:'#f87171' }}>⚠</span>
                    <span style={{ color:'#f87171', fontSize:12 }}>{err}</span>
                  </div>
                )}

                {!result && !loading && !err && (
                  <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
                    justifyContent:'center', color:'#64748B', gap:8 }}>
                    <div style={{ fontSize:28, opacity:.2 }}>🔮</div>
                    <div style={{ fontSize:12 }}>Select a sentence and run</div>
                  </div>
                )}

                {loading && (
                  <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
                    justifyContent:'center', gap:10 }}>
                    <Spinner size={24}/>
                    <div style={{ fontSize:12, color:'#64748B' }}>Running fill-mask…</div>
                  </div>
                )}

                {result && !loading && (() => {
                  const preds     = result.extra?.predictions      || [];
                  const basePreds = result.extra?.base_predictions || null;
                  const input     = result.extra?.input            || text;
                  const compared  = result.extra?.compared         || false;

                  const PredList = ({ items, label, accent }) => (
                    <div style={{ flex:1, minWidth:0 }}>
                      {label && (
                        <div style={{ fontSize:10, fontWeight:700, color: accent,
                          textTransform:'uppercase', letterSpacing:'.08em',
                          fontFamily:'var(--f-mono)', marginBottom:8 }}>{label}</div>
                      )}
                      {items.map((p, i) => {
                        const pct = Math.round(p.score * 100);
                        return (
                          <div key={i} style={{ marginBottom:8, background:'#F1F5FB',
                            border:`1px solid ${i === 0 ? accent + '44' : '#C8D5E8'}`,
                            borderRadius:4, padding:'8px 12px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:5 }}>
                              <span style={{ fontFamily:'var(--f-mono)', fontSize:14, fontWeight:700,
                                color: i === 0 ? accent : '#0F172A' }}>{p.token}</span>
                              <span style={{ fontFamily:'var(--f-mono)', fontSize:12,
                                color: pct >= 50 ? '#34d399' : pct >= 20 ? '#fbbf24' : '#64748B' }}>{pct}%</span>
                            </div>
                            <div style={{ height:2, background:'#C8D5E8', borderRadius:1 }}>
                              <div style={{ width:`${pct}%`, height:'100%', borderRadius:1,
                                background: pct >= 50 ? '#34d399' : pct >= 20 ? '#fbbf24' : '#64748B',
                                transition:'width .4s' }} />
                            </div>
                            <div style={{ fontSize:9, color:'#64748B', marginTop:5, fontFamily:'var(--f-mono)', lineHeight:1.4 }}>
                              {p.sequence}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );

                  return (
                    <>
                      {/* Input echo */}
                      <div style={{ padding:'8px 12px', background:'#F1F5FB', borderRadius:4,
                        fontFamily:'var(--f-mono)', fontSize:11, color:'#334155', lineHeight:1.6,
                        marginBottom:12 }}>
                        {input.split('[MASK]').map((part, i, arr) => (
                          <span key={i}>{part}
                            {i < arr.length - 1 && (
                              <span style={{ background:'rgba(232,71,10,.2)', color:'var(--or)',
                                padding:'1px 4px', borderRadius:3 }}>[MASK]</span>
                            )}
                          </span>
                        ))}
                      </div>

                      {/* Predictions */}
                      {compared && basePreds ? (
                        <div style={{ display:'flex', gap:12 }}>
                          <PredList items={preds}     label="Domain model" accent="var(--or)"/>
                          <div style={{ width:1, background:'#C8D5E8', flexShrink:0 }}/>
                          <PredList items={basePreds} label="Base model"   accent="#60a5fa"/>
                        </div>
                      ) : (
                        <PredList items={preds} accent="var(--or)"/>
                      )}

                      {compare && !compared && result && (
                        <div style={{ fontSize:11, color:'#fbbf24', marginTop:8,
                          padding:'6px 10px', background:'rgba(251,191,36,.07)',
                          border:'1px solid rgba(251,191,36,.2)', borderRadius:4 }}>
                          Base model not found in cache - showing adapted model only.
                        </div>
                      )}

                      {result.duration_ms && (
                        <div style={{ fontSize:10, color:'#64748B', fontFamily:'var(--f-mono)', marginTop:8 }}>
                          {result.duration_ms}ms · v{result.version_number}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </>
      )}
      </ModalBody>
    </Modal>
  );
}

// ── Standard prediction modal ─────────────────────────────────────────────────
function StandardModal({ versionId, featureColumns, targetColumn, familyCode, onClose, runLabel, runNumber }) {
  const [activeTab, setActiveTab] = useState('predict');
  const cols      = Array.isArray(featureColumns) ? featureColumns : [];
  const isText    = !TABULAR_FAMILIES.has(familyCode);
  const hasFields = cols.length > 0;

  const [values,  setValues]  = useState(() => Object.fromEntries(cols.map(c => [c, ''])));
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);
  const [tout,    setTout]    = useState(120);

  const set = (k, v) => setValues(p => ({ ...p, [k]: v }));

  const run = async () => {
    setLoading(true); setErr(null); setResult(null);
    const data = {};
    cols.forEach(k => { if (values[k] !== '') data[k] = values[k]; });
    if (!hasFields || Object.keys(data).length === 0) {
      setErr('Fill in at least one field.'); setLoading(false); return;
    }
    try {
      const res = await apiFetch(`/models/${versionId}/predict`, {
        method: 'POST', body: JSON.stringify({ data, timeout_secs: tout }),
      });
      setResult(res);
    } catch (e) { setErr(cleanError(e.message)); }
    finally { setLoading(false); }
  };

  const probPct = result?.probability != null ? Math.round(result.probability * 100) : null;

  return (
    <Modal title="Single Record Prediction" onClose={onClose} xl>
      <ModalBody>
      <div style={{ display:'flex', borderBottom:'1px solid var(--bd)', marginBottom:16, marginTop:-4 }}>
        {[['predict','▶ Predict'],['history','History']].map(([tid,tlabel]) => (
          <button key={tid} onClick={() => setActiveTab(tid)}
            style={{ padding:'8px 18px', fontSize:12, fontWeight:600, background:'none', border:'none',
              cursor:'pointer', color: activeTab===tid ? 'var(--or)' : '#64748B',
              borderBottom: activeTab===tid ? '2px solid var(--or)' : '2px solid transparent',
              marginBottom:-1 }}>{tlabel}</button>
        ))}
      </div>
      {activeTab === 'history'
        ? <PredictHistory versionId={versionId} />
        : (
      <>
      {(runLabel || runNumber) && (
        <div style={{ marginBottom:16, padding:'8px 12px', background:'#F1F5FB',
          borderRadius:6, borderLeft:'3px solid var(--or)', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:11, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em', fontWeight:600 }}>Run</span>
          {runNumber && <span style={{ fontSize:12, color:'var(--or)', fontFamily:'var(--f-mono)', fontWeight:700 }}>#{runNumber}</span>}
          {runLabel  && <span style={{ fontSize:12, color:'#334155' }}>{runLabel}</span>}
        </div>
      )}
      <div style={{ display:'flex', gap:0, minHeight:400 }}>
        <div style={{ width:320, flexShrink:0, borderRight:'1px solid var(--bd)', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'10px 18px', borderBottom:'1px solid var(--bd)',
            background:'#E4EAF4', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:10, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em' }}>Predicts</span>
            <span style={{ fontFamily:'var(--f-mono)', fontSize:12, color:'var(--or)', fontWeight:600 }}>
              {targetColumn || '-'}
            </span>
            <span style={{ marginLeft:'auto', fontFamily:'var(--f-mono)', fontSize:10, color:'#64748B' }}>
              {familyCode}
            </span>
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'14px 18px', display:'flex', flexDirection:'column', gap:12 }}>
            {!hasFields && (
              <div style={{ color:'#64748B', fontSize:12, padding:'20px 0', textAlign:'center', lineHeight:1.6 }}>
                Feature columns not available.
              </div>
            )}
            {cols.map(col => (
              <div key={col}>
                <div style={{ fontSize:10, fontWeight:600, letterSpacing:'.07em',
                  textTransform:'uppercase', color:'#334155', marginBottom:5 }}>{col}</div>
                {isText
                  ? <textarea rows={4}
                      style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px',
                        background:'#F7F9FC', border:'1px solid var(--bd2)', borderRadius:'8px',
                        color:'#0F172A', fontFamily:'var(--f-ui)', fontSize:12, lineHeight:1.5,
                        resize:'vertical', outline:'none' }}
                      placeholder={`Enter ${col}…`}
                      value={values[col]} onChange={e => set(col, e.target.value)}
                      onFocus={e => e.target.style.borderColor='#E8470A'}
                      onBlur={e  => e.target.style.borderColor='#B0C0D8'}
                    />
                  : <input
                      style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px',
                        background:'#F7F9FC', border:'1px solid var(--bd2)', borderRadius:'8px',
                        color:'#0F172A', fontFamily:'var(--f-ui)', fontSize:12, outline:'none' }}
                      placeholder="Value…"
                      value={values[col]} onChange={e => set(col, e.target.value)}
                      onFocus={e => e.target.style.borderColor='#E8470A'}
                      onBlur={e  => e.target.style.borderColor='#B0C0D8'}
                    />
                }
              </div>
            ))}
          </div>
          <div style={{ padding:'12px 18px', borderTop:'1px solid var(--bd)', display:'flex', gap:8, alignItems:'center' }}>
            <div style={{ display:'flex', flexDirection:'column', gap:3, width:64, flexShrink:0 }}>
              <div style={{ fontSize:9, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em' }}>Timeout</div>
              <input type="number" min={5} max={300} value={tout}
                onChange={e => setTout(+e.target.value)}
                style={{ width:'100%', padding:'5px 7px', background:'#F7F9FC',
                  border:'1px solid var(--bd2)', borderRadius:'4px',
                  color:'#0F172A', fontFamily:'var(--f-mono)', fontSize:12, outline:'none' }} />
            </div>
            <button onClick={run} disabled={loading || !hasFields}
              className="btn btn-primary btn-sm" style={{ flex:1 }}>
              {loading ? <><Spinner size={11}/> Running…</> : '▶ Run'}
            </button>
          </div>
        </div>

        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
          <div style={{ padding:'10px 18px', borderBottom:'1px solid var(--bd)',
            background:'#E4EAF4', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontSize:10, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em' }}>Result</span>
            {result && <button className="btn btn-ghost btn-xs" onClick={() => { setResult(null); setErr(null); }}>Clear</button>}
          </div>
          <div style={{ flex:1, padding:'18px 20px', display:'flex', flexDirection:'column', gap:14 }}>
            {err && (
              <div style={{ display:'flex', gap:8, alignItems:'flex-start', background:'rgba(220,38,38,.08)',
                border:'1px solid rgba(220,38,38,.25)', borderRadius:'8px', padding:'10px 12px' }}>
                <span style={{ color:'#f87171', fontSize:14, flexShrink:0 }}>⚠</span>
                <span style={{ color:'#f87171', fontSize:12, lineHeight:1.5 }}>{err}</span>
              </div>
            )}
            {!result && !loading && !err && (
              <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
                justifyContent:'center', color:'#64748B' }}>
                <div style={{ fontSize:32, marginBottom:10, opacity:.2 }}>◎</div>
                <div style={{ fontSize:12 }}>Fill in the fields and run</div>
              </div>
            )}
            {loading && (
              <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
                justifyContent:'center', gap:10 }}>
                <Spinner size={24}/>
                <div style={{ fontSize:12, color:'#64748B' }}>Running inference…</div>
              </div>
            )}
            {result && !loading && (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ background:'#E4EAF4', border:'1px solid var(--bd)',
                  borderRadius:'8px', padding:'16px 18px', borderLeft:'3px solid var(--or)' }}>
                  <div style={{ fontSize:9, color:'#64748B', textTransform:'uppercase',
                    letterSpacing:'.1em', marginBottom:6 }}>{targetColumn || 'Prediction'}</div>
                  <div style={{ fontFamily:'var(--f-mono)', fontSize:28, fontWeight:700, color:'var(--or)', lineHeight:1 }}>
                    {String(result.prediction ?? '-')}
                  </div>
                </div>
                {probPct != null && (
                  <div style={{ background:'#E4EAF4', border:'1px solid var(--bd)',
                    borderRadius:'8px', padding:'12px 18px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
                      <span style={{ fontSize:10, color:'#64748B', textTransform:'uppercase', letterSpacing:'.08em' }}>Confidence</span>
                      <span style={{ fontFamily:'var(--f-mono)', fontSize:18, fontWeight:600,
                        color: probPct >= 70 ? '#34d399' : probPct >= 40 ? '#fbbf24' : '#f87171' }}>
                        {probPct}%
                      </span>
                    </div>
                    <div style={{ height:5, background:'#C8D5E8', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ width:`${probPct}%`, height:'100%', borderRadius:3,
                        background: probPct >= 70 ? '#34d399' : probPct >= 40 ? '#fbbf24' : '#f87171',
                        transition:'width .5s ease' }} />
                    </div>
                  </div>
                )}
                <div style={{ display:'flex', gap:16 }}>
                  {[['Duration', `${result.duration_ms}ms`], ['Version', `v${result.version_number}`],
                    ['Hash', (result.input_hash?.slice(0,8) || '') + '…']
                  ].map(([l,v]) => (
                    <div key={l} style={{ flex:1, background:'#E4EAF4', border:'1px solid var(--bd)',
                      borderRadius:'4px', padding:'8px 12px' }}>
                      <div style={{ fontSize:9, color:'#64748B', textTransform:'uppercase',
                        letterSpacing:'.08em', marginBottom:3 }}>{l}</div>
                      <div style={{ fontFamily:'var(--f-mono)', fontSize:11, color:'#334155' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </>
      )}
      </ModalBody>
    </Modal>
  );
}


// ── Public export - routes to the right modal based on purpose ────────────────
export function PredictModal({ versionId, featureColumns, targetColumn, familyCode,
                               onClose, runLabel, runNumber, runPurpose }) {
  if (runPurpose === 'DOMAIN_PRETRAIN') {
    return (
      <DaptModal
        versionId={versionId}
        runLabel={runLabel}
        runNumber={runNumber}
        onClose={onClose}
      />
    );
  }
  return (
    <StandardModal
      versionId={versionId}
      featureColumns={featureColumns}
      targetColumn={targetColumn}
      familyCode={familyCode}
      runLabel={runLabel}
      runNumber={runNumber}
      onClose={onClose}
    />
  );
}
